import { Router, Route, Switch, useLocation } from "wouter";
import { useSafeBrowserLocation } from "./lib/router-safe";
import { useEffect } from "react";
import { useAuth } from "./lib/auth";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { BottomNav } from "./components/BottomNav";
import { VerifyBanner } from "./components/VerifyBanner";
import Home from "./pages/Home";
import Marketplace from "./pages/Marketplace";
import ResumeDetail from "./pages/ResumeDetail";
import CompanyDetail from "./pages/CompanyDetail";
import ServiceDetail from "./pages/ServiceDetail";
import JobDetail from "./pages/JobDetail";
import ProjectDetail from "./pages/ProjectDetail";
import RfqDetail from "./pages/RfqDetail";
import ItemDetail from "./pages/ItemDetail";
import Profile from "./pages/Profile";
import CompanyManage from "./pages/CompanyManage";
import Admin from "./pages/Admin";
import AdminVerifications from "./pages/AdminVerifications";
import PostFlow from "./pages/PostFlow";
import Signup from "./pages/Signup";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Messages from "./pages/Messages";
import Wallet from "./pages/Wallet";
import Settings from "./pages/Settings";
import Saved from "./pages/Saved";
import Search from "./pages/Search";
import Verify from "./pages/Verify";
import WorkerVerification from "./pages/WorkerVerification";
import WorkerProfile from "./pages/WorkerProfile";
import HowItWorks from "./pages/HowItWorks";
import LegalDoc from "./pages/LegalDoc";
import LegalGate from "./components/LegalGate";
import Support from "./pages/Support";
import Earnings from "./pages/Earnings";

function ScrollTop() {
  const [loc] = useLocation();
  // NOTE: must NOT implicitly return window.scrollTo(...)'s value — if an
  // extension patches scrollTo to return non-undefined, React treats it as the
  // effect cleanup and later calls it → "c is not a function" on navigation.
  useEffect(() => {
    try {
      window.scrollTo(0, 0);
    } catch {
      /* ignore */
    }
  }, [loc]);
  return null;
}

function Shell() {
  const [loc, navigate] = useLocation();
  const { user } = useAuth();
  useEffect(() => { if (user?.userType === "admin" && loc === "/") navigate("/admin"); }, [user, loc, navigate]);
  const bare = loc === "/login" || loc === "/signup";
  return (
    <div className="min-h-full flex flex-col overflow-x-hidden">
      <ScrollTop />
      {!bare && <Header />}
      {!bare && <VerifyBanner />}
      <main className="flex-1">
        <Switch>
          <Route path="/" component={Home} />
          <Route path="/marketplace" component={Marketplace} />
          <Route path="/p/:id" component={ResumeDetail} />
          <Route path="/c/:id" component={CompanyDetail} />
          <Route path="/s/:id" component={ServiceDetail} />
          <Route path="/j/:id" component={JobDetail} />
          <Route path="/project/:id" component={ProjectDetail} />
          <Route path="/rfq/:id" component={RfqDetail} />
          <Route path="/item/:id" component={ItemDetail} />
          <Route path="/u/:id" component={Profile} />
          <Route path="/company/:id/manage" component={CompanyManage} />
          <Route path="/admin" component={Admin} />
          <Route path="/admin/verifications" component={AdminVerifications} />
          <Route path="/post/:type" component={PostFlow} />
          <Route path="/how-it-works" component={HowItWorks} />
          <Route path="/legal/:kind" component={LegalDoc} />
          <Route path="/support" component={Support} />
          <Route path="/earnings" component={Earnings} />
          <Route path="/signup" component={Signup} />
          <Route path="/login" component={Login} />
          <Route path="/forgot-password" component={ForgotPassword} />
          <Route path="/reset-password" component={ResetPassword} />
          <Route path="/dashboard" component={Dashboard} />
          <Route path="/messages" component={Messages} />
          <Route path="/wallet" component={Wallet} />
          <Route path="/settings" component={Settings} />
          <Route path="/saved" component={Saved} />
          <Route path="/search" component={Search} />
          <Route path="/verify" component={Verify} />
          <Route path="/get-verified" component={WorkerVerification} />
          <Route path="/worker-profile" component={WorkerProfile} />
          <Route>
            <div className="container-x py-24 text-center">
              <h1 className="text-3xl font-extrabold text-navy">404</h1>
              <p className="text-mut mt-2">Page not found.</p>
            </div>
          </Route>
        </Switch>
      </main>
      {!bare && <Footer />}
      {!bare && <BottomNav />}
      {!bare && <LegalGate />}
    </div>
  );
}

export default function App() {
  // Crash-proof clean-URL routing (see lib/router-safe): guards every browser API
  // in try/catch and drives navigation via React state, so page-injecting browser
  // extensions that break addEventListener/pushState can't white-screen the app.
  return (
    <Router hook={useSafeBrowserLocation}>
      <Shell />
    </Router>
  );
}
